import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { ExternalUser } from './shared'

export const SupportExternalUsers: React.FC = () => {
  const { t } = useTranslation()
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const limit = 20

  const { data, isLoading } = useQuery({
    queryKey: ['support-module-users', page, search],
    queryFn: async () => {
      const params = new URLSearchParams()
      params.append('page', String(page))
      params.append('limit', String(limit))
      if (search) params.append('search', search)
      const response = await api.get(`/support/external-users?${params.toString()}`)
      return response.data.data
    },
  })

  const users: ExternalUser[] = data?.users || []
  const total: number = data?.total || 0
  const totalPages = Math.max(1, Math.ceil(total / limit))

  return (
    <div>
      {/* Search */}
      <div className="bg-[#12121a] border border-white/10 rounded-xl p-4 mb-6">
        <input
          type="text"
          placeholder={t('supportModule.searchUsers')}
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1) }}
          className="w-full px-4 py-2 bg-[#1a1a25] border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#006cd4]"
        />
      </div>

      <div className="bg-[#12121a] border border-white/10 rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-gray-400">{t('supportModule.loading')}</div>
        ) : users.length === 0 ? (
          <div className="p-8 text-center text-gray-400">
            <p className="text-lg mb-2">{t('supportModule.noUsers')}</p>
            <p className="text-sm">{t('supportModule.noUsersDesc')}</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-400">{t('supportModule.name')}</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-400">{t('supportModule.email')}</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-400">{t('supportModule.phone')}</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-400">{t('supportModule.externalId')}</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-400">{t('supportModule.identifiedAt')}</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.id} className="border-b border-white/5">
                      <td className="px-4 py-3 font-medium">{user.name || '-'}</td>
                      <td className="px-4 py-3 text-gray-300">{user.email || '-'}</td>
                      <td className="px-4 py-3 text-gray-300">{user.phone || '-'}</td>
                      <td className="px-4 py-3 text-gray-400 font-mono text-sm">{user.external_id || '-'}</td>
                      <td className="px-4 py-3 text-gray-400 text-sm">
                        {new Date(user.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between px-4 py-3 border-t border-white/10">
              <div className="text-sm text-gray-400">
                {t('common.showingOf', { count: users.length, total })}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-3 py-1 bg-white/5 hover:bg-white/10 rounded-lg disabled:opacity-30 transition-colors"
                >
                  {t('common.previous')}
                </button>
                <span className="px-3 py-1 text-sm">
                  {t('common.pageOf', { page, total: totalPages })}
                </span>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="px-3 py-1 bg-white/5 hover:bg-white/10 rounded-lg disabled:opacity-30 transition-colors"
                >
                  {t('common.next')}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
